import { useEffect, useState } from "react";
import { Anchor, Box, Button, Group, Paper, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { createRelease, listStoredApks } from "@utils/api-communication/HeadsetReleasesCli";
import { HARDWARE_FAMILIES, type HardwareFamily, type StoredApk } from "types/headsetReleaseTypes";
import { FamilySlot, isVersionMismatch, storedCopyOf } from "./FamilySlot";
import { withFamily } from "./releaseFormat";
import { notifyError, releaseErrorMessage } from "./notifyError";
import { VERSION_MANAGEMENT_PATH, releasePagePath } from "./releaseRoutes";
import { useFamilyUploads } from "./useFamilyUploads";

export default function NewReleasePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hardware, setHardware] = useState<HardwareFamily[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [storedApks, setStoredApks] = useState<StoredApk[]>([]);
  const { uploads, start, pickStored, remove, markSaved } = useFamilyUploads();

  useEffect(() => {
    listStoredApks().then(setStoredApks).catch(notifyError);
  }, []);

  const selectedUploads = hardware.flatMap((family) => {
    const upload = uploads[family];
    return upload ? [{ family, upload }] : [];
  });
  const version = selectedUploads.find(({ upload }) => upload.inspection)?.upload.inspection
    ?.versionName;
  const storedApksFor = (family: HardwareFamily) =>
    storedApks.filter(
      (stored) =>
        stored.family === family &&
        !stored.releaseExists &&
        (!version || stored.version === version),
    );

  const ready =
    !!version &&
    selectedUploads.length > 0 &&
    selectedUploads.every(
      ({ family, upload }) =>
        upload.uploadId &&
        !upload.error &&
        !isVersionMismatch(upload, version) &&
        !storedCopyOf(upload, storedApksFor(family)),
    );

  const selectFamily = (family: HardwareFamily, selected: boolean) => {
    if (!selected) remove(family);
    setHardware((current) => withFamily(current, family, selected));
  };

  const save = async () => {
    if (!version) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      await createRelease(
        hardware,
        selectedUploads.map(({ family, upload }) => ({
          family,
          uploadId: upload.uploadId as string,
        })),
      );
      markSaved(hardware);
      navigate(releasePagePath(version));
    } catch (error) {
      setSaveError(releaseErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p="lg" maw={1100} mx="auto">
      <Stack gap="lg">
        <Anchor component={Link} to={VERSION_MANAGEMENT_PATH} fw={600}>
          ← {t("headsetReleases.releases")}
        </Anchor>
        <Group gap="sm">
          <Title order={2}>{t("headsetReleases.newRelease")}</Title>
          {version && (
            <Title order={2} c="blue">
              {version}
            </Title>
          )}
        </Group>

        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Title order={4}>{t("headsetReleases.hardware")}</Title>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              {HARDWARE_FAMILIES.map((family) => (
                <FamilySlot
                  key={family}
                  family={family}
                  selected={hardware.includes(family)}
                  onSelectedChange={(selected) => selectFamily(family, selected)}
                  onFile={(file) => start(family, file)}
                  onRemoveUpload={() => remove(family)}
                  onPickStored={(stored) => pickStored(family, stored)}
                  storedApks={storedApksFor(family)}
                  upload={uploads[family]}
                  expectedVersion={version}
                />
              ))}
            </SimpleGrid>
          </Stack>
        </Paper>

        <Group justify="space-between">
          <Text c="red" size="sm">
            {saveError}
          </Text>
          <Button disabled={!ready} loading={saving} onClick={save}>
            {t("headsetReleases.createRelease", { version: version ?? "" })}
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
